import { SlotMachineInfo, SpinInfoResponse, User } from '@cgs/data-access';
import { MachineStateDto, spinResultMapper, startStateMapper } from '@cgs/provider-cgs';
import {
  TrifectaLaunchParamsDto,
  TrifectaAckBalanceResultResponse,
  TrifectaAckResult,
  TrifectaAckResultResponse,
  TrifectaAckSpinResultResponse,
  TrifectaSpinResponse,
  TrifectaSpinResultResponse,
} from '../models/trifecta-dto';

export class TrifectaResponseBuilder {
  constructor(protected launchParams: TrifectaLaunchParamsDto) {}

  public buildSpinResultResponse(json: TrifectaSpinResultResponse): TrifectaSpinResponse {
    const response = spinResultMapper({
      machineState: json.spin,
      userState: {
        userInfo: {
          balance: json.balance,
          cheatUser: this.launchParams.initData.isCheatUser,
          userId: 0,
        },
      },
    });

    return { response, promotionalSpinsData: json.promotionalSpinsData };
  }

  protected buildAckBalanceResultResponse(json: TrifectaAckBalanceResultResponse): User {
    return new User({
      balance: json.balance,
      cheatUser: this.launchParams.initData.isCheatUser,
      userId: 0,
    });
  }

  public buildAckResultResponse(json: TrifectaAckResultResponse): TrifectaAckResult {
    if ('balance' in json) {
      return this.buildAckBalanceResultResponse(json as TrifectaAckBalanceResultResponse);
    }

    if ('spin' in json) {
      return this.buildAckSpinResultResponse(
        json as TrifectaAckSpinResultResponse,
        this.launchParams.balance
      );
    }

    throw new Error('Not implemented');
  }

  protected buildAckSpinResultResponse(
    json: TrifectaAckSpinResultResponse,
    balance: number
  ): SpinInfoResponse {
    return spinResultMapper({
      machineState: json.spin,
      userState: {
        userInfo: {
          balance: balance,
          cheatUser: this.launchParams.initData.isCheatUser,
          userId: 0,
        },
      },
    });
  }

  public buildMachineInfo(spin?: MachineStateDto): SlotMachineInfo {
    const initData = this.launchParams.initData;

    const gameConfig = this.launchParams.initData.gameConfig;
    if (this.launchParams.initData.gameConfig) {
      this.launchParams.volatility = gameConfig.volatility;
      this.launchParams.hitRate = gameConfig.hitRate;
      this.launchParams.buyBonusRtp = gameConfig.buyFeatureRtp;
      this.launchParams.buyFeatureMultiplier = gameConfig.buyFeature;
    }

    const betMultiplier = 1 / initData.maxLines;

    const bets = this.launchParams.betValues.map((bet) => bet * betMultiplier);

    const defaultBet = this.launchParams.defaultBetValue / initData.maxLines;

    const lastMachineState = spin ?? this.launchParams.initData.machineState;

    const startState = startStateMapper({
      betMultiplier: initData.betMultiplier,
      bets: bets,
      defaultBet: defaultBet,
      isCheatUser: initData.isCheatUser,
      configs: initData.configs || null,
      commands: initData.commands || null,
      machineState: lastMachineState,
      maxLines: initData.maxLines ?? null,
      symbols: initData.symbols,
      gameConfig: initData.gameConfig,
    });

    startState.cheatReels = initData.isCheatUser;

    return startState;
  }

  public buildMachineInfoSDK(spin?: MachineStateDto): SlotMachineInfo {
    const initData = this.launchParams.initData;

    const gameConfig = this.launchParams.initData.gameConfig;
    if (this.launchParams.initData.gameConfig) {
      this.launchParams.volatility = gameConfig.volatility;
      this.launchParams.hitRate = gameConfig.hitRate;
      this.launchParams.buyBonusRtp = gameConfig.buyFeatureRtp;
      this.launchParams.buyFeatureMultiplier = gameConfig.buyFeature;
    }

    const decimalMultiplier = Math.pow(10, this.launchParams.currencyDecimals);

    const bets = this.launchParams.betValues.map(
      (bet) => (bet / initData.maxLines) * decimalMultiplier
    );

    const defaultBet = (this.launchParams.defaultBetValue / initData.maxLines) * decimalMultiplier;

    const lastMachineState = spin ?? this.launchParams.initData.machineState;

    const startState = startStateMapper({
      betMultiplier: initData.betMultiplier,
      bets: bets,
      defaultBet: defaultBet,
      isCheatUser: initData.isCheatUser,
      configs: initData.configs || null,
      commands: initData.commands || null,
      machineState: lastMachineState,
      maxLines: initData.maxLines ?? null,
      symbols: initData.symbols,
      gameConfig: initData.gameConfig,
    });

    startState.cheatReels = initData.isCheatUser;

    return startState;
  }
}
